import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { 
  Truck, Star, MapPin, Package, Clock, DollarSign, 
  CheckCircle, ExternalLink, TrendingUp 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFeatures } from '../../contexts/FeatureContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function RunnersTab() {
  const [featuredRunners, setFeaturedRunners] = useState([]);
  const [openJobs, setOpenJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('runners');
  const navigate = useNavigate();
  const { isFeatureEnabled } = useFeatures();

  const runnersEnabled = isFeatureEnabled('runners_service');

  useEffect(() => {
    if (runnersEnabled) {
      loadFeaturedRunners();
      loadOpenJobs();
    }
  }, [runnersEnabled]);

  const loadFeaturedRunners = async () => {
    try {
      const res = await fetch(`${API_URL}/api/connect-runners/runners/featured?limit=20`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setFeaturedRunners(data.runners || []);
      }
    } catch (error) {
      console.error('Failed to load runners:', error);
    }
  };

  const loadOpenJobs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/connect-runners/runners/open-jobs?limit=10`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setOpenJobs(data.jobs || []);
      }
    } catch (error) {
      console.error('Failed to load jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!runnersEnabled) {
    return (
      <div className="text-center py-12">
        <Truck className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <p className="text-gray-500">Runners service is not available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Matrise Runners</h2>
          <p className="text-gray-600">Find verified runners or browse open jobs</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/pickup/post')}>
            <Package className="h-4 w-4 mr-2" />
            Request Runner
          </Button>
          <Button variant="outline" onClick={() => navigate('/pickup/browse')}>
            Browse All Jobs
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="runners">Featured Runners</TabsTrigger>
          <TabsTrigger value="jobs">Open Jobs</TabsTrigger>
        </TabsList>

        <TabsContent value="runners" className="space-y-4">
          {loading ? (
            <div className="text-center py-8">Loading runners...</div>
          ) : featuredRunners.length === 0 ? (
            <Card className="p-8 text-center">
              <Truck className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">No verified runners available yet</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {featuredRunners.map((runner) => (
                <Card key={runner.id} className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/connect/profile/${runner.username}`)}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={runner.profile_picture} />
                        <AvatarFallback>{runner.display_name?.[0] || 'R'}</AvatarFallback>
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold truncate">{runner.display_name}</p>
                          <Badge variant="default" className="gap-1">
                            <Truck className="h-3 w-3" />
                            <CheckCircle className="h-3 w-3" />
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500">@{runner.username}</p>

                        {runner.bio && (
                          <p className="text-sm text-gray-600 mt-2 line-clamp-2">{runner.bio}</p>
                        )}

                        <div className="flex items-center gap-4 mt-3 text-sm">
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 text-yellow-500" />
                            <span className="font-medium">{runner.stats?.rating || 0}</span>
                            <span className="text-gray-500">({runner.stats?.reviews_count || 0})</span>
                          </div>
                          <div className="flex items-center gap-1 text-gray-600">
                            <CheckCircle className="h-4 w-4" />
                            <span>{runner.stats?.completed_jobs || 0} jobs</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1 mt-2 text-sm text-gray-600">
                          <MapPin className="h-4 w-4" />
                          <span>{runner.areas?.city}, {runner.areas?.country}</span>
                        </div>

                        {runner.services && runner.services.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {runner.services.slice(0, 3).map((service, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {service.replace('_', ' ')}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="jobs" className="space-y-4">
          {loading ? (
            <div className="text-center py-8">Loading jobs...</div>
          ) : openJobs.length === 0 ? (
            <Card className="p-8 text-center">
              <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">No open jobs at the moment</p>
              <Button className="mt-4" onClick={() => navigate('/pickup/post')}>
                Post a Job
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {openJobs.map((job) => (
                <Card key={job.id} className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/pickup/job/${job.id}`)}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge>{job.service_category?.replace('_', ' ')}</Badge>
                          <Badge variant="outline">{job.status}</Badge>
                        </div>

                        <p className="font-medium">{job.task_description}</p>

                        <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            <span>{job.pickup_city} → {job.destination_city}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            <span>{job.pickup_date}</span>
                          </div>
                          <div className="flex items-center gap-1 font-medium text-green-600">
                            <DollarSign className="h-4 w-4" />
                            <span>{job.delivery_fee} {job.currency}</span>
                          </div>
                        </div>

                        {job.customer_display_name && (
                          <div className="flex items-center gap-2 mt-2">
                            <Avatar className="h-6 w-6">
                              <AvatarImage src={job.customer_profile_picture} />
                              <AvatarFallback>{job.customer_display_name[0]}</AvatarFallback>
                            </Avatar>
                            <span className="text-sm text-gray-600">
                              Posted by {job.customer_display_name}
                            </span>
                          </div>
                        )}
                      </div>

                      <Button size="sm">
                        View Details
                        <ExternalLink className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
